import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface StagedAttachment {
  filename: string;
  contentBase64: string;
  contentType: string;
  size: number;
}

interface EmailAttachmentsFieldProps {
  value: StagedAttachment[];
  onChange: (next: StagedAttachment[]) => void;
  disabled?: boolean;
  className?: string;
  testIdPrefix?: string;
}

const MAX_FILES = 10;
const MAX_BYTES_PER_FILE = 5 * 1024 * 1024;
const MAX_BYTES_TOTAL = 6 * 1024 * 1024;

const ACCEPTED_MIME = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/zip",
];

const ACCEPT_ATTR = ACCEPTED_MIME.join(",");

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function EmailAttachmentsField({
  value,
  onChange,
  disabled,
  className,
  testIdPrefix = "email-attachments",
}: EmailAttachmentsFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const totalBytes = value.reduce((sum, a) => sum + a.size, 0);

  const handlePick = () => {
    inputRef.current?.click();
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const incoming = Array.from(files);
    const next = [...value];
    let runningTotal = totalBytes;

    for (const file of incoming) {
      if (next.length >= MAX_FILES) {
        toast({
          title: "Too many attachments",
          description: `You can only attach up to ${MAX_FILES} files.`,
          variant: "destructive",
        });
        break;
      }
      if (file.size > MAX_BYTES_PER_FILE) {
        toast({
          title: "File too large",
          description: `${file.name} is over ${MAX_BYTES_PER_FILE / 1024 / 1024}MB.`,
          variant: "destructive",
        });
        continue;
      }
      if (runningTotal + file.size > MAX_BYTES_TOTAL) {
        toast({
          title: "Attachment limit reached",
          description: `Total attachments must stay under ${MAX_BYTES_TOTAL / 1024 / 1024}MB.`,
          variant: "destructive",
        });
        break;
      }
      const contentType = file.type || "application/octet-stream";
      if (!ACCEPTED_MIME.includes(contentType) && !contentType.startsWith("image/")) {
        toast({
          title: "Unsupported file type",
          description: `${file.name} (${contentType}) is not supported.`,
          variant: "destructive",
        });
        continue;
      }
      try {
        const b64 = await fileToBase64(file);
        next.push({
          filename: file.name,
          contentBase64: b64,
          contentType,
          size: file.size,
        });
        runningTotal += file.size;
      } catch (err) {
        toast({
          title: "Could not attach file",
          description: file.name,
          variant: "destructive",
        });
      }
    }

    onChange(next);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = (index: number) => {
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePick}
          disabled={disabled || value.length >= MAX_FILES}
          data-testid={`button-${testIdPrefix}-add`}
        >
          <Paperclip className="w-4 h-4 mr-1.5" />
          Attach files
        </Button>
        {value.length > 0 && (
          <span className="text-xs text-muted-foreground" data-testid={`text-${testIdPrefix}-summary`}>
            {value.length} file{value.length === 1 ? "" : "s"} · {formatBytes(totalBytes)}
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`input-${testIdPrefix}-file`}
        />
      </div>

      {value.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid={`list-${testIdPrefix}`}>
          {value.map((att, i) => {
            const isImage = att.contentType.startsWith("image/");
            return (
              <li
                key={`${att.filename}-${i}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md border bg-muted/30 text-sm"
                data-testid={`item-${testIdPrefix}-${i}`}
              >
                {isImage ? (
                  <ImageIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : (
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <span className="flex-1 truncate" title={att.filename}>
                  {att.filename}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatBytes(att.size)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleRemove(i)}
                  disabled={disabled}
                  data-testid={`button-${testIdPrefix}-remove-${i}`}
                  aria-label={`Remove ${att.filename}`}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
